import ui from "./ui.js";
import materials from "./materials.js";
import * as THREE from "three";

// ─── ИМЕНА МАТЕРИАЛОВ В МОДЕЛИ ───────────────────────────────────────────────
const MAT_NAMES = {
    wood:        ['Wood'],
    carpet:      ['Carpet'],
    deck1:       ['Deck1'],
    deck2:       ['Deck2'],
    deck3:       ['Deck3'],
    deckStairs:  ['DeckStairs'],
    paintedBody: ['PaintedBody'],
    paintedTopBody: ['PaintedTopBody'],
    paintedLine: ['PaintedLine'],
    fabric:      ['FurnitureFabric', 'TechFabric'],     // общий цвет
}

// ─────────────────────────────────────────────────────────────────────────────

export default function options(model, _unused, scene, camera, gui, maxAnisotropy = 1) {
    ui()

    const mats = materials(scene, maxAnisotropy)

    // Контролы lil-gui (заполняются ниже, используются в обработчиках сайдбара
    // для синхронизации значения picker'а)
    const guiColorCtrl = {}

    // ── Кэш мешей по группам ─────────────────────────────────────────────────
    function buildMeshCache(nameList) {
        const meshes = []
        const lower = nameList.map(n => n.toLowerCase())
        model.traverse(child => {
            if (!child.isMesh || !child.material) return
            const match = mat => lower.includes((mat?.name || '').toLowerCase())
            if (Array.isArray(child.material)) {
                child.material.forEach((m, idx) => {
                    if (match(m)) meshes.push({ child, idx })
                })
            } else if (match(child.material)) {
                meshes.push({ child, idx: null })
            }
        })
        return meshes
    }

    function applyMatToCache(cache, newMat) {
        cache.forEach(({ child, idx }) => {
            if (idx !== null) {
                const arr = child.material.slice()
                arr[idx] = newMat
                child.material = arr
            } else {
                child.material = newMat
            }
        })
    }

    function applyColorToCache(cache, hexColor) {
        const color = new THREE.Color(hexColor)
        cache.forEach(({ child, idx }) => {
            const mat = idx !== null ? child.material[idx] : child.material
            if (mat) mat.color.set(color)
        })
    }

    const caches = {
        wood:        buildMeshCache(MAT_NAMES.wood),
        carpet:      buildMeshCache(MAT_NAMES.carpet),
        deck1:       buildMeshCache(MAT_NAMES.deck1),
        deck2:       buildMeshCache(MAT_NAMES.deck2),
        deck3:       buildMeshCache(MAT_NAMES.deck3),
        deckStairs:  buildMeshCache(MAT_NAMES.deckStairs),
        paintedBody: buildMeshCache(MAT_NAMES.paintedBody),
        paintedTopBody: buildMeshCache(MAT_NAMES.paintedTopBody),
        paintedLine: buildMeshCache(MAT_NAMES.paintedLine),
        fabric:      buildMeshCache(MAT_NAMES.fabric),
    }

    // ── Универсальный биндер сайдбара (radio + custom-color picker) ──────────
    function bindMatGroup(name, map) {
        const cache = caches[name]
        const radios = document.querySelectorAll(`input[name="${name}Material"]`)
        // дефолт = :checked, иначе первый свотч в группе
        const def = document.querySelector(`input[name="${name}Material"]:checked`) || radios[0]
        if (def) { def.checked = true }
        if (def && map[def.value]) applyMatToCache(cache, map[def.value])
        radios.forEach(r => r.addEventListener('change', e => applyMatToCache(cache, map[e.target.value])))
    }

    function bindColorGroup(name, fallbackCaches = null) {
        const targetCaches = fallbackCaches || [caches[name]]
        const radios = document.querySelectorAll(`input[name="${name}Color"]`)
        // дефолт = :checked, иначе первый свотч в группе
        const def = document.querySelector(`input[name="${name}Color"]:checked`) || radios[0]
        if (def) { def.checked = true; targetCaches.forEach(c => applyColorToCache(c, def.value)) }
        radios.forEach(r => r.addEventListener('change', e => {
            targetCaches.forEach(c => applyColorToCache(c, e.target.value))
            const ctrl = guiColorCtrl[name]
            if (ctrl) ctrl.setValue(e.target.value)
        }))
    }

    // ── WOOD ─────────────────────────────────────────────────────────────────
    bindMatGroup('wood', {
        redDark:   mats.wood.redDark,
        walnut:    mats.wood.walnut,
        fine:      mats.wood.fine,
        quartered: mats.wood.quartered,
    })

    // ── CARPET (паттерн + цвет тинтом) ───────────────────────────────────────
    bindMatGroup('carpet', {
        checker:     mats.carpet.checker,
        herringbone: mats.carpet.herringbone,
        arrows:      mats.carpet.arrows,
    })
    // Carpet Color убран из UI — цвет ковра зафиксирован.
    // Смена паттерна заменяет материал, поэтому тинт переприменяем после каждого change.
    const CARPET_COLOR = '#c9a274'
    applyColorToCache(caches.carpet, CARPET_COLOR)
    document.querySelectorAll('input[name="carpetMaterial"]').forEach(r =>
        r.addEventListener('change', () => applyColorToCache(caches.carpet, CARPET_COLOR)))

    // ── DECK (один UI-control → меняет 4 материала Deck1/2/3/DeckStairs) ────
    function applyDeckVariant(variant) {
        applyMatToCache(caches.deck1,      mats.deck1[variant])
        applyMatToCache(caches.deck2,      mats.deck2[variant])
        applyMatToCache(caches.deck3,      mats.deck3[variant])
        applyMatToCache(caches.deckStairs, mats.deckStairs[variant])
    }
    const deckRadios = document.querySelectorAll('input[name="deckMaterial"]')
    const defaultDeck = document.querySelector('input[name="deckMaterial"]:checked') || deckRadios[0]
    if (defaultDeck) { defaultDeck.checked = true; applyDeckVariant(defaultDeck.value) }
    document.querySelectorAll('input[name="deckMaterial"]').forEach(item => {
        item.addEventListener('change', e => applyDeckVariant(e.target.value))
    })

    // ── PAINTED LINE / BODY ──────────────────────────────────────────────────
    bindColorGroup('paintedBody')
    bindColorGroup('paintedTopBody')
    bindColorGroup('paintedLine')

    // ── FABRIC: один цвет на FurnitureFabric + TechFabric ────────────────────
    bindColorGroup('fabric', [caches.fabric])

    // ── CUSTOM COLOR PICKERS внутри сайдбара (gradient swatch) ───────────────
    document.querySelectorAll('.custom-color-input').forEach(input => {
        const label = input.closest('.color-radio')
        const target = input.dataset.target              // "carpetColor" | "paintedBodyColor" | ...
        const groupName = target.replace(/Color$/, '')   // "carpet" | "paintedBody" | ...
        const cache = caches[groupName]
        if (!cache) return
        const radios = document.querySelectorAll(`input[name="${target}"]`)

        const panel = label.closest('.panel')
        const dataSpan = panel?.previousElementSibling?.querySelector('.accordion-data')

        const activate = () => {
            radios.forEach(r => { r.checked = false })
            document.querySelectorAll(`.custom-color-input[data-target="${target}"]`)
                .forEach(i => i.closest('.color-radio').classList.remove('active'))
            label.classList.add('active')
            if (dataSpan) dataSpan.textContent = 'Custom'
        }

        input.addEventListener('input', e => {
            activate()
            applyColorToCache(cache, e.target.value)
            const ctrl = guiColorCtrl[groupName]
            if (ctrl) ctrl.setValue(e.target.value)
        })
        radios.forEach(r => r.addEventListener('change', () => label.classList.remove('active')))
    })

    // ── lil-gui: ВСЕ доступные настройки ─────────────────────────────────────
    if (gui) {
        const matFolder = gui.addFolder('Материалы')

        // Селекторы пресетов (через объект-стейт)
        const woodOptions       = { redDark: 'redDark', walnut: 'walnut', fine: 'fine', quartered: 'quartered' }
        const carpetPatterns    = { checker: 'checker', herringbone: 'herringbone', arrows: 'arrows' }
        const deckColors        = { brown: 'brown', gray: 'gray', light: 'light', red: 'red' }

        const initVal = (sel, fallback) => document.querySelector(sel)?.value || fallback
        const state = {
            wood:   initVal('input[name="woodMaterial"]:checked',   'redDark'),
            carpet: initVal('input[name="carpetMaterial"]:checked', 'checker'),
            deck:   initVal('input[name="deckMaterial"]:checked',   'brown'),
            carpetColor:      CARPET_COLOR,
            paintedBodyColor: initVal('input[name="paintedBodyColor"]:checked', '#f5e5c2'),
            paintedTopBodyColor: initVal('input[name="paintedTopBodyColor"]:checked', '#f8f8f6'),
            paintedLineColor: initVal('input[name="paintedLineColor"]:checked', '#ec8d09'),
            fabricColor:      initVal('input[name="fabricColor"]:checked',      '#2b2f33'),
        }

        // ── Пресеты материалов ──
        const woodFolder = matFolder.addFolder('Wood')
        woodFolder.add(state, 'wood', woodOptions).name('Тип').onChange(v => {
            applyMatToCache(caches.wood, mats.wood[v])
            const r = document.querySelector(`input[name="woodMaterial"][value="${v}"]`)
            if (r) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })) }
        })

        const carpetFolder = matFolder.addFolder('Carpet')
        carpetFolder.add(state, 'carpet', carpetPatterns).name('Паттерн').onChange(v => {
            applyMatToCache(caches.carpet, mats.carpet[v])
            const r = document.querySelector(`input[name="carpetMaterial"][value="${v}"]`)
            if (r) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })) }
        })
        guiColorCtrl.carpet = carpetFolder.addColor(state, 'carpetColor').name('Цвет')
            .onChange(v => applyColorToCache(caches.carpet, v))

        const deckFolder = matFolder.addFolder('Deck')
        deckFolder.add(state, 'deck', deckColors).name('Цвет').onChange(v => {
            applyDeckVariant(v)
            const r = document.querySelector(`input[name="deckMaterial"][value="${v}"]`)
            if (r) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })) }
        })

        const paintedFolder = matFolder.addFolder('Painted')
        guiColorCtrl.paintedBody = paintedFolder.addColor(state, 'paintedBodyColor').name('Body')
            .onChange(v => applyColorToCache(caches.paintedBody, v))
        guiColorCtrl.paintedTopBody = paintedFolder.addColor(state, 'paintedTopBodyColor').name('Top Body')
            .onChange(v => applyColorToCache(caches.paintedTopBody, v))
        guiColorCtrl.paintedLine = paintedFolder.addColor(state, 'paintedLineColor').name('Line')
            .onChange(v => applyColorToCache(caches.paintedLine, v))

        const fabricFolder = matFolder.addFolder('Fabric (Furniture + Tech)')
        guiColorCtrl.fabric = fabricFolder.addColor(state, 'fabricColor').name('Цвет')
            .onChange(v => applyColorToCache(caches.fabric, v))

        matFolder.open()
        woodFolder.open()
        carpetFolder.open()
        deckFolder.open()
        paintedFolder.open()
        fabricFolder.open()
    }
}
